import React from 'react';
import cx from 'classnames';
import cloneDeep from 'lodash.clonedeep';
import get from 'lodash.get';
import TextInput from '../Student/AssessmentItems/TextInput';
import ReadAloud from '../Student/AssessmentItems/ReadAloud';
import ReadAloudVideo from '../Student/AssessmentItems/ReadAloudVideo';
import RANItems from '../Student/Dyslexia/RANItems';
import SingleWord from '../Student/Items/SingleWord';
import SplitItem from '../Student/Dyslexia/SplitItem';
import Video from '../Student/Dyslexia/Video';
import {findFilenames, findSyllableFilename, getUserData} from "../../services/API";
import {getStoryContentLocale, isMarkupInstructionForStory} from "../../services/playMarkupSequence";
import {logSessionInfo} from '../../services/logger';
import {COPY_STRINGS} from '../../constants/translatedCopy';
import {aosTaskTypes, NO_RESPONSE, AWAITABLE_TRIGGERS, phraseTypes, isAwaitableIntroductionTrigger, TriggerActions, TTS_EVENTS, FORMATIVE_ITEM_TYPES, FORMATIVE_ONLY_ITEM_TYPES, deferPoolNarrationToScript} from '../../constants/constants';
import {parseSkipTranslationTags, parseTextWithPhonemes, calculateSpeechTimeout, escapeSsml} from "../../services/util";
import {splitPhraseForTapDisplay} from "../../services/NLP";
import {getIdleTimeouts, getIdleTimeoutsForStory} from '../../constants/itemIdleTimeouts';
import Quiz from '../Quiz/Quiz';
import Transition from './Transition';
import NextButton from './NextButton';
import PictureStoryPage from '../Student/Practice/PictureStoryPage';
import StoryIntro from './StoryIntro';
import WordPartDeletion from './Phrase/PhraseActivities/WordPartDeletion/index';
import ChangeOnePartWrapper from './Phrase/PhraseActivities/ChangeOnePart/ChangeOnePartWrapper';
import UpAndDownTutor from './Phrase/PhraseActivities/UpAndDown/UpAndDownTutor';
import ElkoninTutor from './Phrase/PhraseActivities/Elkonin/ElkoninTutor';
import NonsenseWordRepetition from './Phrase/PhraseActivities/NonsenseWordRepetition/NonsenseWordRepetition';
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions.js";
import LetterFliesWrapper from './Phrase/PhraseActivities/LetterFlies/LetteryfliesWrapper';
import { withCurrentStoryWordMetadata } from '../../context/CurrentStoryMetadataContext';
import UpAndDownWrapper from './Phrase/PhraseActivities/UpAndDown/UpAndDownWrapper';
import Retell from '../Retell/Retell';
import FreeAnswer from '../FreeAnswer/FreeAnswer';
import BlendingSelection from '../BlendingSelection/BlendingSelection';
import Spelling from '../Spelling/Spelling';
import VisualAttention from '../VisualAttention/VisualAttention';
import ModularMultipleChoice from '../ModularMultipleChoice/ModularMultipleChoice';
import FormativeMultipleChoice from '../FormativeMultipleChoice/FormativeMultipleChoice';
import { MULTI_SELECT_MODE } from '../FormativeMultipleChoice/formativeMultipleChoiceConstants';
import Bucketing from '../Bucketing/Bucketing';
import Sequencing from '../Sequencing/Sequencing';
import SentenceBuilder from '../SentenceBuilder/SentenceBuilder';
import * as Sentry from '@sentry/browser';
import { parsePhrasesWithTriggers } from './instructionTriggers';

const statusOptions = {
  ERROR: "error",
  SUCCESS: "success",
  UNREAD: "unread",
  PROCESSING: "processing",
  INTERVENTION: "intervention",
  FLAGGED: "flagged",
  BOUNDARY_WORD: "boundaryWord",
}

// for phrase types in this array we will ignore veerbal instructions in the phrase metadata
//  if we're playing the FTUE experience
const IGNORE_INSTRUCTIONS_FTUE_TYPES = [
  phraseTypes.UP_AND_DOWN,
  phraseTypes.CHANGE_ONE_PART,
  phraseTypes.ELKONIN,
]

// this is a way to temporarily disable phrase level instructions entirely for activity types
// which don't support this yet but intend to in the future
const IGNORE_INSTRUCTIONS_ENTIRELY_TYPES = [
  phraseTypes.ELKONIN,
]

const IGNORE_INSTRUCTIONS_NON_FTUE_TYPES = [
  phraseTypes.UP_AND_DOWN,
  phraseTypes.LETTER_FLIES,
]

/** Pause before the next speech segment after a visual trigger (show/highlight/etc.). */
const INTRO_POST_VISUAL_TRIGGER_GAP_MS = 1500;

// TT-1006 tap-to-read auto-fit: if a phrase overflows the available screen
// HEIGHT, step --tap-font-scale down (never below TAP_MIN_FONT_SCALE) until it
// fits. Because the line width is the full card (not font-relative), a smaller
// font flows MORE words per line → fewer lines, so it shrinks as little as
// possible rather than wasting horizontal space. Screen-driven, not story-length.
const TAP_MIN_FONT_SCALE = 0.6;
const TAP_FIT_STEP = 0.08;

// Assessment types that support item-level metadata overriding testlet-level defaults
// (options, images, instructions — but NOT sentenceBuilder-specific fields like wordDisplay)
const ITEM_FIELD_CARRYTHROUGH_TYPES = [
  phraseTypes.LISTENING_COMPREHENSION,
  phraseTypes.VOCABULARY,
  phraseTypes.GRAPHEME_PHONEME_CONVERSION,
  ...FORMATIVE_ITEM_TYPES,
];

// Assessment types that should render with full viewport height
const FULLHEIGHT_ASSESSMENT_TYPES = [
  'blendingSelection',
  'visualAttention',
  phraseTypes.LISTENING_COMPREHENSION,
  phraseTypes.SENTENCE_BUILDER,
  phraseTypes.VOCABULARY,
  phraseTypes.GRAPHEME_PHONEME_CONVERSION,
  'retell',
  ...FORMATIVE_ITEM_TYPES,
];

// Assessment types that should render full-width (bleed to edges)
const FULLBLEED_ASSESSMENT_TYPES = [
  ...FULLHEIGHT_ASSESSMENT_TYPES,
];
/** Pause before the next speech segment after a [split]-delimited phrase (between clauses, same visual). */
const INTRO_POST_SPLIT_PHRASE_GAP_MS = 500;
/** Pause after [show] / [hide] before the next instruction line speaks (let UI settle). */
export const INTRO_POST_SHOW_BEFORE_SPEECH_MS = 1000;

let itemTypeHistory = [];

function isShowIntroductionTrigger(triggerAction) {
  return triggerAction === TriggerActions.Show;
}

function checkPhraseFtue(types) {
  if(!types){
    return false;
  }
  for (const type of itemTypeHistory) {
    if (types.includes(type)) return false;
  }
  return true;
}

class Word extends React.Component {
  constructor (props){
    super(props);
    this.handleClick = this.handleClick.bind(this);
  }

  handleClick(){
    // do nothing if the word is not clickable
    if(!this.props.clickable){
      return;
    }

    if(this.props.tapMode && this.props.onTapToReadTap){
      this.props.onTapToReadTap(this.props.index);
      return;
    }

    //if the word is clickable and we have a setPlacement function, we are in the reading tutor context
    //  and should set the placement. Otherwise, we are in the correction context and should toggle the status
    // TODO dis-entangle props for setPlacement and toggling status
    if(this.props.setPlacement){
      //this.props.setPlacement(this.props.index);
      //do nothing
      // TODO: re-enable click placement, both here and within the definition of "isClickable",
      // within the getMarkedPhrase method
    } else {

      // toggle between "error", "success", and "unread" for edge words,
      //  and "error" and "success" for all other words
      let newStatus = "";
      if(this.props.status === statusOptions.ERROR){
        if(this.props.canGrayOut(this.props.phraseIndex, this.props.index)){
          newStatus = statusOptions.UNREAD;
        } else {
          newStatus = statusOptions.SUCCESS;
        }
      } else if(this.props.status === statusOptions.SUCCESS){
        newStatus = statusOptions.ERROR;
      } else {
        newStatus = statusOptions.SUCCESS;
      }
      this.props.updateErrors(this.props.index, newStatus);
    }
  }

  // returns a function which triggers a toggle on an example word.
  // To be used in in context help, currently dead code
  getDemoClickFunction(){
    return function () {
      this.toggleStatus();
      window.setTimeout(this.toggleStatus, 3000);
    }
  }

  render(){
    let wordClass = "word " + this.props.status + this.props.className;
    if(this.props.highlighted){
      wordClass += " highlighted";
    }
    if(this.props.clickable){
      wordClass += " clickable";
    }
    if(this.props.ghostNext){
      wordClass += " ghostNext";
    }
    if(this.props.tapMode){
      wordClass += " tapMode";
    }
    if(this.props.isEarlyReader && this.props.isEarlyReader()) {
      wordClass += " earlyReader";
    }

    const accessibleClickAttributes = this.props.clickable ?
      {...doNotUseAccessibleStaticElementInteractions({onClick: this.handleClick})} : {};

    // add special selector to 6th word for in context help
    return (<span className={wordClass} data-tut={this.props.index == 6 ? "word" : null} {...accessibleClickAttributes}>{this.props.word}</span>);
    /*if(this.props.index == 6){
      if(this.props.setDemoClick){
        this.props.setDemoClick(this.getDemoClickFunction().bind(this));
      }
    }*/
  }
}

class Phrase extends React.Component {
  constructor (props){
    super(props);
    const introduction = this.isSessionFirstActivity() ? get(this.props, 'phraseMetaData.introductions.textIntroductions.full') : get(this.props, 'phraseMetaData.introductions.textIntroductions.short')
    const startPhraseDisplay = introduction || (!this.shouldIgnoreInstructions() && get(this.props, 'phraseMetaData.instructions.text')) || this.props.text

    this.state = {
      text: startPhraseDisplay,
      metaData: this.parseMetaData(this.props.phraseMetaData),
      willHandlePause: false,
      pendingTriggerCallback: null,
      currentTriggerData: [],
      tapFontScale: 1, // TT-1006: lowered by the auto-fit safeguard for long phrases
    }
    this.tapPhraseRef = React.createRef();
    this.runTapFit = this.runTapFit.bind(this);
    this.updateErrors = this.updateErrors.bind(this);
    this.handleStartOfPhrase = this.handleStartOfPhrase.bind(this);
    this.isSessionFirstActivity = this.isSessionFirstActivity.bind(this);
    this.speakWithPhoneme = this.speakWithHumanPhoneme.bind(this);
    this.speakPhonemes = this.speakHumanPhonemes.bind(this);
    this.checkSetPreviousPhraseErrors = this.checkSetPreviousPhraseErrors.bind(this);
    this.setWillHandlePause = this.setWillHandlePause.bind(this);
    this.onFinishVerbalInstructions = this.onFinishVerbalInstructions.bind(this);
    this.onFinishVerbalIntroductions = this.onFinishVerbalIntroductions.bind(this);
    this.onTriggerActionComplete = this.onTriggerActionComplete.bind(this);
    this.removeTtsCallStartedEventListener = this.removeTtsCallStartedEventListener.bind(this);
    this.handleTtsCallStartedEvent = this.handleTtsCallStartedEvent.bind(this);
    this.resetVerbalTimeoutLogs = this.resetVerbalTimeoutLogs.bind(this);
    this.getCurrentWordMetadata = this.getCurrentWordMetadata.bind(this);

    // Timeout safeguard for verbal introductions/instructions to prevent infinite hangs
    this.verbalSpeechTimeoutId = null;
    // TT-1312: the verbal sequence the safeguard is watching (see speakVerbalInstructionsWithTriggers).
    // When the safeguard fires it cancels the run, so the stalled sequence's late callbacks
    // cannot speak more audio or arm the recognizer a second time.
    this.verbalSpeechRun = null;
    this.verbalTimeoutLogs = {
      parsePhrasesWithTriggersInfo: [],
      speakPhrasesInfo: [],
      speakVerbalInstructionsInfo: [],
      ttsTriggeredTexts: [],
      verbalInstructionsTexts: [],
    };
    this.addedTtsCallStartedEventListener = false;
  }

  componentDidMount(){
    this.loggedTestletIds = [];
    if(this.props.isActive){
      this.handleStartOfPhrase()
    }
    this.runTapFit();
  }

  componentDidUpdate(prevProps, prevState){
    //For pages that have multiple phrases per page, hit the start of phrase stuff when they become active.
    if(!prevProps.isActive && this.props.isActive){
      this.handleStartOfPhrase();
    }
    // if prev props's video url is different than the current prop's videoURL OR
    // prev props's phraseMetadata.assetURL is different than the current prop's assetURL
    // AND if the new URL is not null
    if(prevProps.phraseMetaData && this.props.phraseMetaData){
      if((this.props.phraseMetaData.videoURL && (prevProps.phraseMetaData.videoURL != this.props.phraseMetaData.videoURL))  ||
      (this.props.phraseMetaData.assetURL && (prevProps.phraseMetaData.assetURL != this.props.phraseMetaData.assetURL))){
        this.setState({
          text: this.props.text,
          metaData: this.parseMetaData(this.props.phraseMetaData),
        }, this.handleStartOfPhrase.bind(this));
      }
    }

    // pause detection handling for all phrases
    if(this.props.isActive){
      if(prevProps.paused !== this.props.paused){
        if(this.props.paused){
          if(!this.state.willHandlePause){
            const interventionInProgress = this.props.handleDefaultPauseDetected();
            if(!interventionInProgress){
              console.log("🔵 calling done handling pause")
              this.props.doneHandlingPause();
            }
          }else{
            // phrase item component should call doneHandlingPause to tell reader manager that it's done handling the pause
            console.log("🔵 component will handle pause");
          }
        }else{
          // resumed
        }
      }
    }

    // TT-1006: keep the tap-to-read phrase within one screen. Reset the auto-fit
    // scale when a new phrase arrives (so each phrase starts at full size), then
    // re-measure and shrink if it overflows.
    if (this.props.isTapToRead) {
      const textChanged = prevState && prevState.text !== this.state.text;
      const becameActive = !prevProps.isActive && this.props.isActive;
      if ((textChanged || becameActive) && this.state.tapFontScale !== 1) {
        this.setState({ tapFontScale: 1 }); // re-fit on the following update
      } else {
        this.runTapFit();
      }
    }
  }

  componentWillUnmount() {
    // store the item type so that we can know for instructional purposes whether the child has seen this item type before
    itemTypeHistory.push(this.state.metaData.type);
    
    // Clear any pending verbal speech timeout to prevent memory leaks and orphaned callbacks
    if (this.verbalSpeechTimeoutId) {
      clearTimeout(this.verbalSpeechTimeoutId);
      this.verbalSpeechTimeoutId = null;
    }
    // A late onStart / onEnd from this phrase's speech must not re-arm the safeguard or arm the
    // (global) recognizer once the phrase is gone.
    if (this.verbalSpeechRun) {
      this.verbalSpeechRun.cancelled = true;
      this.verbalSpeechRun = null;
    }
    this.removeTtsCallStartedEventListener();
    this.loggedTestletIds = [];
  }

  setWillHandlePause(willHandlePause){
    this.setState({ willHandlePause });
  }

  /**
   * TT-1006 tap-to-read auto-fit. Measures the rendered phrase against the reading
   * card's available height and, if it overflows, steps --tap-font-scale down
   * (never up, never below TAP_MIN_FONT_SCALE) until it fits one screen. Since the
   * line width is the full card, each shrink flows more words per line (fewer
   * lines), so it stops at the largest size that fits. Only ever shrinks, so it
   * converges; the scale is reset to 1 per new phrase in componentDidUpdate.
   * No-ops outside active tap mode and when layout is unmeasurable (e.g. jsdom,
   * where clientHeight is 0 → available <= 0).
   */
  runTapFit(){
    if (!this.props.isTapToRead || !this.props.isActive) return;
    const el = this.tapPhraseRef.current;
    if (!el || typeof window === 'undefined' || !window.getComputedStyle) return;
    const reader = el.closest('.reader');
    if (!reader) return;
    // Available height = reader content box (its height minus its own padding,
    // which already reserves room for the top offset and the bottom mic/next-button).
    const cs = window.getComputedStyle(reader);
    const padTop = parseFloat(cs.paddingTop) || 0;
    const padBottom = parseFloat(cs.paddingBottom) || 0;
    const available = reader.clientHeight - padTop - padBottom;
    if (available <= 0) return; // not laid out yet (safe in tests)
    // +2px tolerance so sub-pixel rounding / the underline overhang don't force
    // an unnecessary shrink step.
    if (el.scrollHeight > available + 2 && this.state.tapFontScale > TAP_MIN_FONT_SCALE) {
      this.setState({
        tapFontScale: Math.max(TAP_MIN_FONT_SCALE, this.state.tapFontScale - TAP_FIT_STEP),
      });
    }
  }

  onTriggerActionComplete(){
    if (this.state.pendingTriggerCallback) {
      const cb = this.state.pendingTriggerCallback;
      this.setState({ pendingTriggerCallback: null }, cb);
    }
  }

  shouldIgnoreInstructions() {
    const { phraseMetaData } = this.props;
  
    if (!phraseMetaData) {
      return false;
    }

    const { type, playFTUE } = phraseMetaData;
    const isFTUE = checkPhraseFtue(type) || playFTUE;
    const ignore_ftue_instructions = IGNORE_INSTRUCTIONS_FTUE_TYPES.includes(type);
    const ignore_entire_instructions = IGNORE_INSTRUCTIONS_ENTIRELY_TYPES.includes(type);
    const ignore_non_ftue_instructions = IGNORE_INSTRUCTIONS_NON_FTUE_TYPES.includes(type);

    if(ignore_entire_instructions){
      return true;
    }
    if (isFTUE && ignore_ftue_instructions) {
      return true;
    }
    if (!isFTUE && ignore_non_ftue_instructions) {
      return true;
    }
  
    return false;
  }

  parseMetaData(phraseMetaData){
    let metaData = cloneDeep(phraseMetaData) || {};
    // set the metadata for current render
    metaData = metaData.introductions || metaData.instructions || metaData;
    // An item's instructions block carries its own presentation type — 'mmc' on every
    // Formative select item — and collapsing onto it drops the item's interaction type,
    // which is what decides whether the response is encoded before it is sent. Only
    // Formative items diverge this way, so legacy types keep the instructions type.
    if (!phraseMetaData?.introductions && FORMATIVE_ONLY_ITEM_TYPES.includes(phraseMetaData?.type)) {
      metaData.type = phraseMetaData.type;
    }
    metaData.isVideo = metaData.videoURL || metaData.type === "readAloudVideo";
    if (phraseMetaData && phraseMetaData.letterDisplay) {
      metaData.letterDisplay = phraseMetaData.letterDisplay;
    }
    if (phraseMetaData && phraseMetaData.wordDisplay) {
      metaData.wordDisplay = phraseMetaData.wordDisplay;
    }
    if (phraseMetaData && phraseMetaData.version) {
      metaData.version = phraseMetaData.version;
    }
    // Carry through item-level fields so components always have data to render —
    // triggers control visibility, not data availability.
    // Guard with !metaData.X so testlet-level instruction fields (e.g. example
    // options/wordDisplay used during FTUE) aren't overwritten by item data.
    if (phraseMetaData && ITEM_FIELD_CARRYTHROUGH_TYPES.includes(phraseMetaData.type)) {
      // `options: []` on instructions is truthy but empty — still carry item options through for render.
      if (
        (!metaData.options || metaData.options.length === 0) &&
        phraseMetaData.options &&
        phraseMetaData.options.length > 0
      ) {
        metaData.options = phraseMetaData.options;
      }
      if (!metaData.videoURL && phraseMetaData.videoURL) metaData.videoURL = phraseMetaData.videoURL;
      if (!metaData.imageURL && phraseMetaData.imageURL) metaData.imageURL = phraseMetaData.imageURL;
      if (!metaData.writtenInstructions && phraseMetaData.writtenInstructions) metaData.writtenInstructions = phraseMetaData.writtenInstructions;
      if (metaData.readOptionsAloud == null && phraseMetaData.readOptionsAloud != null) metaData.readOptionsAloud = phraseMetaData.readOptionsAloud;
      if (metaData.verticalOptions == null && phraseMetaData.verticalOptions != null) metaData.verticalOptions = phraseMetaData.verticalOptions;
      if (!metaData.backgroundImageUrl && phraseMetaData.backgroundImageUrl) metaData.backgroundImageUrl = phraseMetaData.backgroundImageUrl;
      if (
        (!metaData.dropZones || metaData.dropZones.length === 0) &&
        phraseMetaData.dropZones &&
        phraseMetaData.dropZones.length > 0
      ) {
        metaData.dropZones = phraseMetaData.dropZones;
      }
      if (!metaData.mode && phraseMetaData.mode) metaData.mode = phraseMetaData.mode;
    }
    if (phraseMetaData && phraseMetaData.type === phraseTypes.SENTENCE_BUILDER) {
      if (!metaData.writtenInstructions && phraseMetaData.writtenInstructions) metaData.writtenInstructions = phraseMetaData.writtenInstructions;
      if (!metaData.wordDisplay && phraseMetaData.wordDisplay) metaData.wordDisplay = phraseMetaData.wordDisplay;
    }

    if(metaData.isVideo && getUserData().sessionData.useIpadFallbacks){
      let {verbalInstructions=""} = metaData;
      metaData.verbalInstructions = `${verbalInstructions} ${COPY_STRINGS.intervention_strings.tap_play}`;
    }

    return metaData;
  }
  onFinishVerbalIntroductions() {
    // callback logic to remove the introduction just presented from metadata
    // replace with instructions if any, otherwise, replace with story text
    
    // Clear the timeout safeguard since the callback fired successfully
    if (this.verbalSpeechTimeoutId) {
      clearTimeout(this.verbalSpeechTimeoutId);
      this.verbalSpeechTimeoutId = null;
    }
    
    const withoutIntroductions = { ...this.props.phraseMetaData }
    const hasInstructions = get(this.props, 'phraseMetaData.instructions')
    delete withoutIntroductions.introductions
    const instructionText = hasInstructions && !this.shouldIgnoreInstructions()
      ? get(this.props, 'phraseMetaData.instructions.text')
      : null;
    const text =
      instructionText != null && String(instructionText).trim() !== ''
        ? instructionText
        : (this.props.text ?? '');
    this.props.enableRepeatButton();
    this.setState({
      text: text,
      metaData: this.parseMetaData(withoutIntroductions), // update state.metadata to instructions metadata
    }, () => {
      this.props.onVerbalIntroductionsComplete?.();
      this.handleStartOfPhrase();
    });
  }

  /**
   * @param {Object} [run] - the verbal sequence that just ended (see speakVerbalInstructionsWithTriggers).
   */
  onFinishVerbalInstructions(run) {
    // callback logic to remove instructions after repsenting from metadata
    // replace with story text
    
    // Clear the timeout safeguard since the callback fired successfully
    if (this.verbalSpeechTimeoutId) {
      clearTimeout(this.verbalSpeechTimeoutId);
      this.verbalSpeechTimeoutId = null;
    }
    
    // Check if we have queued firstItemVerbalInstructions to play next — only when this run held
    // its arm back for them. handleStartOfPhrase decided that from props when the run started, and
    // props can have changed since: a run that already armed would have the clip play into the open
    // mic and arm a second time.
    const firstItemVerbalInstructions = run?.advanceQueuesSpeech
      ? this.props.phraseMetaData.firstItemVerbalInstructions
      : null;

    if (firstItemVerbalInstructions) {
      // Update visual to show the actual phrase (not the example anymore)
      // But keep verbalInstructions active so we play the next set
      this.setState({
        text: this.props.text,
        metaData: {
          ...this.props.phraseMetaData,
          verbalInstructions: firstItemVerbalInstructions,
        }
      }, () => {
        // Now play the first item's verbal instructions
        this.props.disableRepeatButton();
        this.speakVerbalInstructionsWithTriggers(firstItemVerbalInstructions, (innerRun) => {
          // After these finish, clear timeout and we're done
          if (this.verbalSpeechTimeoutId) {
            clearTimeout(this.verbalSpeechTimeoutId);
            this.verbalSpeechTimeoutId = null;
          }
          // Sampled before the setState below replaces state.metaData. That is the first-item block
          // set above — raw item metadata, not the parsed block — and on purpose: it is what this
          // run's own executeCallback reads.
          const gates = this.captureArmGates();
          const needsArm = this.firstItemRunNeedsArm(innerRun);
          if (needsArm && gates.transition) {
            // executeCallback starts the transition in place of the advance; onTransitionEnd arms.
            this.startTransition();
            return;
          }
          this.props.enableRepeatButton();
          this.setState({
            text: this.props.text,
            metaData: this.props.phraseMetaData,
          }, () => {
            if (needsArm) this.armAfterSafeguard(gates);
          });
        }, true);
      });
    } else {
      // If the outer run held its arm back for a first-item clip that is no longer there (gone from
      // props since handleStartOfPhrase read it), nothing else will arm, so arm here. Not from an
      // inactive phrase, same as the safeguard.
      const gates = run?.advanceQueuesSpeech && this.props.isActive ? this.captureArmGates() : null;
      if (gates?.transition) {
        // An outer script with no spoken text advances without reaching executeCallback, whose
        // transition branch would have started it. Start it here, before the swap below hides the
        // parsed block's transition; onTransitionEnd arms.
        this.startTransition();
        return;
      }
      // Original behavior for other types
      this.props.enableRepeatButton();
      this.setState({
        text: this.props.text,
        metaData: this.props.phraseMetaData, // update state.metadata to of the actual phrase item metadata
      }, () => {
        if (gates) this.armAfterSafeguard(gates);
      });
    }
  }

  /**
   * (Re)starts the verbal-speech safeguard's no-progress window for `run`. TT-1312: this used to be
   * one deadline for the whole script, armed before the first TTS request, so TTS latency,
   * human-phoneme mp3 loads, <break>s and inter-clip gaps all spent a word-count budget that
   * counted none of them — it fired on healthy-but-slow sequences, then opened the mic while the
   * rest of the script was still playing. Now every sign of progress (a segment or phoneme item
   * starting, a clip starting to play) restarts the window, sized for the step in progress, so it
   * only fires when nothing has moved for longer than any one step's own guard (16s TTS request,
   * 10s app-sound load, Coach's clip-duration watchdog). Paused while Phrase waits on awaitable
   * triggers ([show:repeatOverlayInput], [show:options] FTUE, [click:…]).
   * @param {Object} run - The sequence from speakVerbalInstructionsWithTriggers.
   * @param {string} stepText - Text of the step now in progress, for the duration heuristic.
   * @param {string} reason - What progressed; reported if the window expires.
   */
  armVerbalSpeechSafeguard(run, stepText, reason) {
    if (this.verbalSpeechTimeoutId) {
      clearTimeout(this.verbalSpeechTimeoutId);
      this.verbalSpeechTimeoutId = null;
    }

    const windowMs = calculateSpeechTimeout(
      stepText,
      15000, // 15 second buffer for outer safeguard
      30000  // 30 second minimum
    );
    run.lastProgressAt = Date.now();
    run.lastProgressReason = reason;
    run.lastStepText = stepText;
    run.progressCount += 1;
    if (process.env.NODE_ENV !== 'production') {
      console.log(`Verbal speech no-progress window: ${Math.round(windowMs / 1000)}s (${reason})`);
    }

    // One timer per phrase, owned by this.verbalSpeechRun: (re)arming it here replaces whatever
    // window was pending, including a superseded run's.
    this.verbalSpeechTimeoutId = setTimeout(() => {
      this.verbalSpeechTimeoutId = null;
      this.onVerbalSpeechStalled(run, windowMs);
    }, windowMs);
  }

  /**
   * Restarts the safeguard window when `run` makes progress. Ignored for a run that has finished,
   * been cancelled, been superseded by a newer sequence, or is paused on an awaitable trigger.
   */
  noteVerbalSpeechProgress(run, stepText, reason) {
    if (!run || run !== this.verbalSpeechRun || run.done || run.cancelled || run.awaitingTrigger) return;
    this.armVerbalSpeechSafeguard(run, stepText, reason);
  }

  /**
   * The sequence made no progress for a full window: stop it, then end it the way its final clip's
   * executeCallback would have.
   */
  onVerbalSpeechStalled(run, windowMs) {
    const now = Date.now();
    if (process.env.NODE_ENV !== 'production') {
      console.error('❌ Verbal speech made no progress for', windowMs, 'ms. Forcing progression.');
      console.warn('Verbal timeout Info logs', JSON.stringify(this.verbalTimeoutLogs));
    }
    // Message and keys kept so existing alerts keep matching; `text` is still the whole script.
    // `timeoutMs` is now the no-progress window, not a whole-script budget — `mode` tells the two
    // apart in dashboards.
    const details = {
      text: run.text,
      timeoutMs: windowMs,
      mode: 'noProgress',
      lastProgress: run.lastProgressReason,
      stalledStep: run.lastStepText,
      progressCount: run.progressCount,
      msSinceLastProgress: now - run.lastProgressAt,
      msSinceSequenceStart: now - run.startedAt,
    };
    Sentry.captureEvent({
      level: 'warning',
      message: 'VERBAL_SPEECH_TIMEOUT',
      extra: details,
    });
    logSessionInfo('verbal_speech_timeout', details);
    this.resetVerbalTimeoutLogs();

    // Cancel first: every remaining step, and the late onEnd of whatever is stuck, checks this and
    // does nothing. That is what keeps more instruction audio from playing into an open mic, and
    // what stops a late onEnd from arming a second time (resetTranscript + enableRecording + timer
    // restart mid-recording).
    run.cancelled = true;
    if (this.verbalSpeechRun === run) this.verbalSpeechRun = null;

    // A phrase that is no longer the current one does nothing more. Speech and
    // props.startPhrase are both global, and even the advance can speak (an item with
    // firstItemVerbalInstructions queues its next clip from there), so any of them would act on
    // whatever phrase is on screen now. If the phrase becomes active again, handleStartOfPhrase
    // starts its instructions over.
    if (!this.props.isActive) return;

    // Gates sampled BEFORE the advance: state.metaData is still the parsed block here — the same
    // thing the final clip's executeCallback reads — where isVideo is derived and
    // transition/textIntroductions are hoisted out of the nested instructions object. Afterwards
    // state holds raw phraseMetaData, in which a RAN item's instructions.transition is invisible.
    const gates = run.armAfterAdvance ? this.captureArmGates() : null;

    // Stop whatever is still playing or pending (TTS clip, human phoneme mp3) and clear the speech
    // queue, before anything opens the mic. The same forced-progression abort ReaderManager uses.
    try {
      this.props.speech?.abortSpeech?.();
    } catch (error) {
      console.error('❌ abortSpeech failed in verbal speech safeguard:', error);
    }

    // Mirrors executeCallback: a transition item starts its transition rather than advancing, and
    // onTransitionEnd arms. Before, a safeguard on a transition item never armed at all.
    if (gates?.transition) {
      this.startTransition();
      return;
    }

    run.advanceMetaData();
    // TT-1312: advanceMetaData swaps the display to the item but arms nothing, so before this
    // the mic never opened when the safeguard fired — the student read and nothing recorded.
    // Arming after the advance also means the item is already on screen: this app is React 16,
    // where setState outside a React event handler commits synchronously, so advanceMetaData()
    // has fully applied by the time it returns.
    if (run.armAfterAdvance) this.armAfterSafeguard(gates);
  }

  /**
   * Snapshot of executeCallback's gates, taken while state.metaData is still the parsed block.
   * @returns {{ownedByVideo: boolean, textIntroductions: boolean, transition: boolean}}
   */
  captureArmGates(){
    const metaData = this.state.metaData || {};
    return {
      ownedByVideo: !!(metaData.isVideo && metaData.type !== "readAloudVideo"),
      textIntroductions: !!metaData.textIntroductions,
      transition: !!metaData.transition,
    };
  }

  /**
   * Arms the recognizer after the safeguard has forced the sequence to end, applying the same gates
   * executeCallback applies inline. Only runs with armAfterAdvance call this: on the introductions
   * path the advance runs handleStartOfPhrase, which owns the arm itself (TT-1312). No isFinal
   * check, unlike executeCallback: a forced end is always the end of the sequence. Also the
   * first-item fallback arms in onFinishVerbalInstructions. Callers handle gates.transition first:
   * a transition item starts its transition instead of arming.
   * @param {{ownedByVideo: boolean, textIntroductions: boolean, transition: boolean}} gates - from captureArmGates,
   *   sampled before the advance.
   */
  armAfterSafeguard(gates){
    // onVideoEnd owns a video item's arm, and a text-introduction item is not armed on completion
    // either — the same two branches in executeCallback advance without arming.
    if(gates?.ownedByVideo || gates?.textIntroductions) return;
    // Sourced from props, not state: the advance writes raw phraseMetaData into state, and reading
    // props doesn't depend on the setState having committed.
    if(!this.shouldAutoStartPhrase(this.props.phraseMetaData?.type)) return;
    if(!this.props.startPhrase) return;
    this.props.startPhrase();
  }

  /**
   * The first-item clip's run is the only arm on that item: the outer run held its arm back for
   * it. Normally its final clip arms in executeCallback, but a script with no spoken text (only
   * triggers) has no final clip and advances without arming, so its advance has to arm instead.
   * @param {Object} run - the first-item clip's run.
   * @returns {boolean} true when nothing has made this run's arm decision and the phrase is active.
   */
  firstItemRunNeedsArm(run){
    // A cancelled run was already armed by the safeguard; finalClipEnded means executeCallback
    // made the decision.
    if(!run || run.cancelled || run.finalClipEnded) return false;
    // Like the safeguard: an inactive phrase has been advanced past (Next, intervention skip) and
    // startPhrase is ReaderManager-global. The phrase now on screen arms when it becomes active.
    return !!this.props.isActive;
  }

  /**
   * @param {string} verbalInstructions - The script to speak.
   * @param {Function} advanceMetaData - Ends the sequence; called once, on completion or by the
   *   safeguard, with the run.
   * @param {boolean} armAfterAdvance - TT-1312: when the safeguard forces the end, arm the
   *   recognizer once the advance has run. False for introductions (handleStartOfPhrase arms) and
   *   for a run whose advance queues first-item instructions instead of landing on the item.
   * @param {{advanceQueuesSpeech?: boolean}} [options] - advanceQueuesSpeech: this run's advance
   *   starts another verbal sequence (firstItemVerbalInstructions) instead of landing on the item,
   *   so its normal end must not arm either — the next sequence's end does.
   */
  speakVerbalInstructionsWithTriggers(verbalInstructions, advanceMetaData, armAfterAdvance, { advanceQueuesSpeech = false } = {}) {
    if (process.env.NODE_ENV !== 'production') {
      console.log('🎤 Starting verbal introductions/instructions:', verbalInstructions);
    }
    
    // TT-1312: the sequence the safeguard watches, and cancels if it stops making progress.
    const run = {
      text: verbalInstructions,
      advanceMetaData: null,
      armAfterAdvance,
      advanceQueuesSpeech,
      startedAt: Date.now(),
      progressCount: 0,
      cancelled: false,
      done: false,
      awaitingTrigger: false,
      finalClipEnded: false,
    };

    // Wrap advanceMetaData to ensure it's only called once (completion or safeguard)
    const safeAdvanceMetaData = () => {
      if (run.done) {
        console.warn('⚠️ advanceMetaData already called, skipping duplicate call');
        return;
      }
      run.done = true;
      
      try {
        advanceMetaData(run);
      } catch (error) {
        console.error('❌ Error in advanceMetaData:', error);
        logSessionInfo('verbal_speech_advance_metadata_error', {
          error: error.message,
          stack: error.stack,
        });
      }
    };

    run.advanceMetaData = safeAdvanceMetaData;
    this.verbalSpeechRun = run;
    this.armVerbalSpeechSafeguard(run, '', 'sequenceStarted');

    // listen to ttsCallStartedEvent to check if the tts call was triggered for the verbal instructions
    if (!this.addedTtsCallStartedEventListener) {
      window.addEventListener(TTS_EVENTS.CALL_STARTED, this.handleTtsCallStartedEvent);
      this.addedTtsCallStartedEventListener = true;
    }

    if (this.verbalTimeoutLogs.verbalInstructionsTexts.length > 5) {
      this.resetVerbalTimeoutLogs(); // reset the logs if the number of verbal instructions texts is greater than 5
    }
    this.verbalTimeoutLogs.verbalInstructionsTexts.push(verbalInstructions); // add the verbal instructions to the logs to check if the tts call was triggered for the verbal instructions

    const phrases = this.parsePhrasesWithTriggers(verbalInstructions);
    
    // Prefetch text segments to warm the TTS cache (reduces latency when each segment is spoken).
    // IMPORTANT: Must stay in sync with speakVerbalInstructions phoneme handling.
    // This block mirrors that function's splitting logic so that:
    // 1. <phoneme voice="human" .../> tags are never sent to TTS (they use pre-recorded audio)
    // 2. Cache keys align with what speak() will request
    // If speakVerbalInstructions changes its phoneme/skipTranslation handling, update this too.
    const textSegments = phrases
      .filter(p => p.text && p.text.trim())
      .map(p => p.text.trim());
    if (textSegments.length > 0 && this.props.speech?.prefetch) {
      const bilingualMode = getUserData().bilingualMode;

      textSegments.forEach(seg => {
        // In bilingual mode, first split by skipTranslation tags, then by phonemes.
        // In non-bilingual mode, pass undefined for skipTranslation to match speakVerbalInstructions.
        const skipParsed = bilingualMode
          ? parseSkipTranslationTags(seg)
          : [{ textToSpeak: seg, skipTranslation: undefined }];

        skipParsed.forEach(item => {
          if (!item.textToSpeak || !item.textToSpeak.trim()) return;

          // parseTextWithPhonemes returns [] if no phonemes, so we can check length directly
          // (mirrors the pattern in speakVerbalInstructions)
          const phonemeParsed = parseTextWithPhonemes(item.textToSpeak, item.skipTranslation);
          if (phonemeParsed.length) {
            // Only prefetch text-type portions; phoneme items resolve to pre-recorded audio
            phonemeParsed
              .filter(p => p.type === 'text' && p.value && p.value.trim())
              .forEach(p => {
                this.props.speech.prefetch([p.value], { skipTranslation: p.skipTranslation });
              });
          } else {
            // No phonemes - prefetch the whole segment
            this.props.speech.prefetch([item.textToSpeak], { skipTranslation: item.skipTranslation });
          }
        });
      });
    }
    
    this.speakPhrases(phrases, safeAdvanceMetaData, run);
  }

  handleTtsCallStartedEvent (event) {
    const inText = event?.detail?.inText;
    if (this.verbalTimeoutLogs.verbalInstructionsTexts.includes(inText)) { // only track tts calls for the verbal instructions and add to the logs
      this.verbalTimeoutLogs.ttsTriggeredTexts.push(inText);
    }
  };

  removeTtsCallStartedEventListener() { // cleanup the event listener
    if (this.addedTtsCallStartedEventListener) {
      window.removeEventListener(TTS_EVENTS.CALL_STARTED, this.handleTtsCallStartedEvent);
      this.addedTtsCallStartedEventListener = false;
    }
  }

  resetVerbalTimeoutLogs() {
    this.verbalTimeoutLogs = {
      parsePhrasesWithTriggersInfo: [],
      speakPhrasesInfo: [],
      speakVerbalInstructionsInfo: [],
      ttsTriggeredTexts: [],
      verbalInstructionsTexts: [],
    };
  }

  parsePhrasesWithTriggers(verbalInstructions) {
    try {
      const phrases = parsePhrasesWithTriggers(verbalInstructions);
      this.verbalTimeoutLogs.parsePhrasesWithTriggersInfo.push({
        time: Date.now(),
        verbalInstructions: verbalInstructions,
        phrases: phrases,
        successText: 'parsePhrasesWithTriggersInfo ended successfully',
      });

      return phrases;
    } catch (error) {
      this.verbalTimeoutLogs.parsePhrasesWithTriggersInfo.push({
        time: Date.now(),
        errText: 'Error in parsePhrasesWithTriggers: '+error?.message,
        errObj: error,
        verbalInstructions: verbalInstructions,
      });
      return [{ text: verbalInstructions, trigger: null, triggerAction: null, triggerData: [] }];
    }
  }

  speakPhrases(phrases, advanceMetaData, run) {
    try {
      const speakNextPhrase = (fromTrigger) => {
        // TT-1312: a cancelled run (safeguard fired, or the phrase unmounted) never speaks again —
        // this is reached from the [show:…] trigger delay as well as from a clip's end.
        if (run?.cancelled) return;
        if (phrases.length === 0) {
          this.setState({ currentIntroductionTrigger: null, currentTriggerData: [] }, () => {
            advanceMetaData();
          });
          return;
        }

        const { text, trigger, triggerAction, triggerData, pauseAfterSpeech } = phrases.shift();
        if (trigger) {
          // AWAITABLE_TRIGGERS: optional escape hatch — full raw bracket strings if isAwaitableIntroductionTrigger
          // ever misses a legacy token; empty today so includes() is a no-op unless the array is repopulated.
          const isAwaitableTrigger =
            AWAITABLE_TRIGGERS.includes(trigger) ||
            isAwaitableIntroductionTrigger(triggerAction, triggerData, this.state.metaData?.type);
          if (isAwaitableTrigger) {
            // Waiting on the student is not a stall: pause the safeguard until the trigger completes.
            if (run) run.awaitingTrigger = true;
            if (this.verbalSpeechTimeoutId && (!run || run === this.verbalSpeechRun)) {
              clearTimeout(this.verbalSpeechTimeoutId);
              this.verbalSpeechTimeoutId = null;
            }
            const remainingText = phrases
              .map((p) => p.text)
              .filter(Boolean)
              .join(' ')
              .trim();
            this.setState({
              currentIntroductionTrigger: triggerAction || trigger,
              currentTriggerData: triggerData || [],
              pendingTriggerCallback: () => {
                if (run) run.awaitingTrigger = false;
                this.noteVerbalSpeechProgress(run, remainingText, 'triggerCompleted');
                const isFromTrigger = true;
                if (isShowIntroductionTrigger(triggerAction)) {
                  setTimeout(
                    () => speakNextPhrase(isFromTrigger),
                    INTRO_POST_SHOW_BEFORE_SPEECH_MS
                  );
                } else {
                  speakNextPhrase(isFromTrigger);
                }
              },
            });
          } else {
            this.noteVerbalSpeechProgress(run, '', 'trigger');
            this.setState({
              currentIntroductionTrigger: triggerAction || trigger,
              currentTriggerData: triggerData || [],
            }, () => {
              const isFromTrigger = true;
              if (isShowIntroductionTrigger(triggerAction)) {
                setTimeout(
                  () => speakNextPhrase(isFromTrigger),
                  INTRO_POST_SHOW_BEFORE_SPEECH_MS
                );
              } else {
                speakNextPhrase(isFromTrigger);
              }
            });
          }
        } else {
          const onEnd = () => {
            // If this phrase was launched after a "trigger", delay calling speakNextPhrase
            // This gives the user time to process what was said along with the visuals associated with the "trigger"
            // i.e. in new spelling task
            // ...ALSO never include delay if we're speaking the final phrase, even after a trigger.
            // This prevents confusing visuals where the mic turns on, but we delay updating the reader UI.
            const isFinalPhrase = phrases.length === 0;
            let gapMs = 0;
            if (!isFinalPhrase) {
              if (fromTrigger) {
                gapMs = INTRO_POST_VISUAL_TRIGGER_GAP_MS;
              } else if (pauseAfterSpeech) {
                gapMs = INTRO_POST_SPLIT_PHRASE_GAP_MS;
              }
            }
            if (gapMs > 0) {
              setTimeout(() => {
                speakNextPhrase();
              }, gapMs);
            } else {
              // Otherwise, move on immediately so we don't have unnecessary pauses
              speakNextPhrase();
            }
          };
          if (text) {
            const isFinal = !phrases.some(phrase => phrase.text);
            this.noteVerbalSpeechProgress(run, text, 'segmentStarted');
            this.speakVerbalInstructions(text, onEnd, isFinal, run);
          } else {
            onEnd();
          }
        }
      };

      speakNextPhrase();
      this.verbalTimeoutLogs.speakPhrasesInfo.push({
        time: Date.now(),
        phrases: phrases,
        successText: 'speakPhrasesInfo ended successfully',
      });
    } catch (error) {
      this.verbalTimeoutLogs.speakPhrasesInfo.push({
        time: Date.now(),
        errText: 'Error in speakPhrases: '+error?.message,
        errObj: error,
        phrases: phrases,
      });
    }
  }

  async speakVerbalInstructions(verbalInstructions, verbalInstructionsCallback, isFinal, run){
    try {
    // Track if callback has been called to prevent double-calling from both onEnd and onError
    let callbackFired = false;
    // TT-1312: a clip starting to play is progress for the safeguard.
    const onClipStart = () => this.noteVerbalSpeechProgress(run, verbalInstructions, 'clipStarted');
    
    // Shared callback logic for both success (onEnd) and error (onError) paths
    const executeCallback = (fromError = false) => {
      if (callbackFired) {
        console.warn('⚠️ Verbal instructions callback already fired, skipping duplicate call');
        return;
      }
      callbackFired = true;
      // TT-1312: the safeguard gave up on this sequence and already ended (and, if it could, armed)
      // it. This is the stalled clip's late end: arming again would reset the transcript,
      // re-enable recording and restart the phrase timers mid-recording.
      if (run?.cancelled) {
        console.warn('⚠️ Verbal instructions ended after the safeguard fired, ignoring');
        return;
      }
      // The final clip makes this run's arm decision below, whichever branch it takes.
      if (isFinal && run) run.finalClipEnded = true;

      if (fromError) {
        console.warn('⚠️ Verbal instructions completed via onError path');
      } else {
        console.log('✅ Verbal instructions onEnd callback fired');
      }
      
      try {
        // Only hit the startPhrase callback if there is no videoURL.
        // Otherwise let the video take care of it when it's done playing.
        // Keep these branches in sync with captureArmGates / armAfterSafeguard, which make the same
        // decision when the safeguard forces the end of the sequence.
        if(this.state.metaData.isVideo && this.state.metaData.type !== "readAloudVideo"){
          verbalInstructionsCallback();
        }else if(this.state.metaData.textIntroductions){
          verbalInstructionsCallback();
        }else if(this.state.metaData.transition){
          this.startTransition();
          this.verbalTimeoutLogs.speakVerbalInstructionsInfo.push({
            time: Date.now(),
            verbalInstructions: verbalInstructions,
            errText: 'Called startTransition for verbal instructions',
            errObj: null,
            metaData: this.state.metaData,
          });
        }else {
          // Not when the advance is about to start another clip (firstItemVerbalInstructions):
          // arming here would record that clip's audio, and its own end arms again — a second
          // startPhrase that resets the transcript and restarts the phrase timers.
          if(this.shouldAutoStartPhrase(this.props.phraseMetaData.type) && isFinal && !run?.advanceQueuesSpeech){
            this.props.startPhrase();
          }
          verbalInstructionsCallback();
        }
      } catch (error) {
        console.error('❌ Error in verbal instructions callback:', error);
        logSessionInfo('verbal_instructions_callback_error', {
          error: error.message,
          stack: error.stack,
        });
        // Still try to call the callback to prevent hang
        try {
          verbalInstructionsCallback();
        } catch (callbackError) {
          console.error('❌ Failed to call verbalInstructionsCallback:', callbackError);
        }
      }
    };
    
    // Success callback
    const onEnd = () => executeCallback(false);
    
    // Error callback - treat audio errors as if speech completed
    // This ensures the story progresses even when audio fails
    const onError = (error) => {
      console.error('❌ Audio error during verbal instructions:', error);
      logSessionInfo('verbal_instructions_audio_error', {
        error: error?.message || 'Unknown error',
        text: verbalInstructions,
      });
      executeCallback(true);
    };

    // if we're ignoring verbal instructions, just call onEnd and return without speaking
    //  this will happen if we're playing the FTUE for a phrase type
    //  which skips verbal instructions on FTUE
    if(this.shouldIgnoreInstructions()){
      onEnd();
      return;
    }

    if(getUserData().bilingualMode){
      // Get "skip translation" parsed instructions
      let translationParsedInstructions = parseSkipTranslationTags(verbalInstructions);
      if(this.props.skipIntros) translationParsedInstructions = [{textToSpeak: "Skipping Instructions"}];

      // Loop though the translation parsed instructions
      for (const instruction of translationParsedInstructions) {
        if (run?.cancelled) return;
        this.noteVerbalSpeechProgress(run, instruction.textToSpeak, 'speechItemStarted');
        // Parse out any phoneme tags for this chunk of translationParsedInstructions
        const phonemeParsedInstructions = parseTextWithPhonemes(instruction.textToSpeak, instruction.skipTranslation);

        if (phonemeParsedInstructions.length) {
          // Play the phoneme parsed instruction (it will include any skipTranslation tags found from translationParsedInstructions)
          // Then move on to the next chunk of translationParsedInstructions
          await new Promise((resolve) => {
            this.speakWithHumanPhoneme(phonemeParsedInstructions, resolve, onError, run)
          });
        } else {
          const instructionSpeech = {
            textToSpeak: instruction.textToSpeak,
            passThroughSpeechTags: true,
            skipTranslation: instruction.skipTranslation,
            onError: onError,
            onStart: onClipStart,
          }

          await this.props.speech.speakWithPromise(instructionSpeech);
        }
      }

      onEnd()
    } else {

      // parse the instruction, break into an array of objects with normal text and phoneme text
      const speechItemsWithHumanPhonemes = parseTextWithPhonemes(verbalInstructions)

      // this speak with human phoneme is here to support English for now, when we are ready to
      // suppport Spanish, refactor this with the above bilingualMode section
      if(speechItemsWithHumanPhonemes.length){
        this.speakWithHumanPhoneme(speechItemsWithHumanPhonemes, onEnd, onError, run)
      }else if(verbalInstructions){
        if(this.props.skipIntros) verbalInstructions = "Skipping Instructions";
        let instructionSpeech = {
          textToSpeak: verbalInstructions,
          passThroughSpeechTags: true,
          onEnd: onEnd,
          onError: onError,
          resolveAbort: onEnd,
          onStart: onClipStart,
        }
        // passing empty string to .speak will cause a fatal error
        // What if we use speakSequence here?
        this.props.speech.speak(instructionSpeech);
      }else{
        // catch cases where an empty string is passed, it should still
        // call onEnd to advance the metadata to continue the story.
        onEnd()
      }
    }
    } catch (error) {
      this.verbalTimeoutLogs.speakVerbalInstructionsInfo.push({
        time: Date.now(),
        verbalInstructions: verbalInstructions,
        errText: 'Error in speakVerbalInstructions: '+error?.message,
        errObj: error,
        isFinal: isFinal,
      });
    }
  }

  /**
   * Takes a list of speech items with human spoken phonemes and call speech.speak on it if it's text string,
   * If it is a phoneme string, it gets the audio files and pass to speakPhonemes to play the audio.
   * @param {Array} speechItems An array of parsed speech item objects { type, value }
   * @param {Function} speakCallback A callback function which will be called after finishing speaking the speech items
   * @param {Function} errorCallback Optional callback function which will be called if audio playback fails
   * @param {Object} [run] TT-1312: the verbal sequence being spoken, if any (see speakVerbalInstructionsWithTriggers)
   * @returns nothing
   */
  speakWithHumanPhoneme(speechItems, speakCallback, errorCallback = null, run = null){
    // TT-1312: the safeguard stopped this sequence. The aborted item's settle (app-sound watchdog,
    // resolveAbort) still lands here and must not start the next item. Settle the caller instead —
    // every caller of a cancelled run is a no-op, and the bilingual loop's promise resolves.
    if(run?.cancelled){
      speakCallback();
      return;
    }

    if(!speechItems.length){
      speakCallback();
      return
    }

    const item = speechItems.shift();
    // Each item starting — including a pre-recorded phoneme whose mp3 is still loading — is progress.
    this.noteVerbalSpeechProgress(run, item.type === 'text' ? item.value : '', 'speechItemStarted');
    const onClipStart = () => this.noteVerbalSpeechProgress(run, item.value, 'clipStarted');
    const onEnd = () => {
      this.speakWithHumanPhoneme(speechItems, speakCallback, errorCallback, run);
    }
    
    const onError = (error) => {
      console.error('Error in speakWithHumanPhoneme:', error);
      if (errorCallback) {
        errorCallback(error);
      } else {
        // If no error callback provided, treat as success to prevent hang
        onEnd();
      }
    }

    // Pre-recorded phoneme/syllable audio follows the CONTENT language (the story's tags),
    // not the student's interface locale — so English content gets English phonemes and es-mx
    // content gets Spanish audio regardless of the tutor/interface locale.
    const storyLocale = getStoryContentLocale(this.props.story);

    // use amira speak if it's text
    if(item.type === 'text'){
      this.props.speech.speak({
        textToSpeak: item.value,
        passThroughSpeechTags: true,
        skipTranslation: item.skipTranslation,
        onEnd,
        onError,
        resolveAbort: onEnd,
        onStart: onClipStart,
      });
    }

    // use human recorded voice if it's a phoneme
    if(item.type === 'phoneme'){
      const urls = findFilenames(item.value, storyLocale);
      // if no urls found, just move to the next speech item
      if(!urls.length) onEnd();
      // speak all phoneme audios, then move to the next speech item
      console.log('speaking human phonemes, phoneme:', item.value, 'urls:', urls);
      this.speakHumanPhonemes(urls, onEnd, onError, run);
    }

    // use human recorded voice if it's a Spanish syllable, else fall back to TTS
    if(item.type === 'syllable'){
      // Voice the syllable via TTS. Used both when no verified asset exists and when a
      // verified-present asset fails to LOAD from the CDN, so the syllable is never silent.
      const speakSyllableViaTts = () => {
        // The mp3 attempt may have been aborted by the safeguard before this fallback runs.
        if(run?.cancelled) return;
        this.noteVerbalSpeechProgress(run, item.value, 'speechItemStarted');
        this.props.speech.speak({
          textToSpeak: `<speak>${escapeSsml(item.value)}</speak>`,
          passThroughSpeechTags: true,
          skipTranslation: item.skipTranslation,
          onEnd,
          onError,
          resolveAbort: onEnd,
          onStart: onClipStart,
        });
      };

      const url = findSyllableFilename(item.value, storyLocale);
      if(url){
        // Verified-present pre-recorded syllable (see spanishSyllableAssets.js). Guard the
        // load: an allowlisted key whose mp3 is not yet on the CDN (deploy-lagged/corrupt)
        // would otherwise play as silence, then advance. onLoadError fires before onfinish
        // (see Speech.playAppSounds), so fall back to TTS instead of silence — matching the
        // repeat/idle replay path in ModularMultipleChoice.playMarkupSequence.
        let loadFailed = false;
        this.props.speech.speakAudioFile({
          url,
          onStart: onClipStart,
          onLoadError: () => { loadFailed = true; },
          onfinish: () => {
            if(loadFailed){
              speakSyllableViaTts();
            }else{
              onEnd();
            }
          },
        });
      }else{
        // No verified pre-recorded asset for this syllable — it is either not yet
        // deployed to the CDN, or a ñ/ü syllable that must not be voiced as its
        // accent-stripped form (e.g. "ña" as "na"). Fall back to TTS so the syllable
        // is still voiced rather than silent.
        speakSyllableViaTts();
      }
    }
  }

  /**
   * Takes a list of human spoken phoneme urls and play it
   * @param {Array.<string>} phonemeURLs An array of phoneme URLs
   * @param {Function} speakCallback A callback function which will be called after finishing playing the list of audio files
   * @param {Function} errorCallback Optional callback function which will be called if audio playback fails
   * @param {Object} [run] TT-1312: the verbal sequence being spoken, if any (see speakVerbalInstructionsWithTriggers)
   * @returns nothing
   */
  speakHumanPhonemes(phonemeURLs, speakCallback, errorCallback = null, run = null){
    // TT-1312: an aborted mp3 still settles through the app-sound watchdog; don't play the next
    // one, just settle the caller (a no-op for a cancelled run).
    if(run?.cancelled){
      speakCallback();
      return;
    }

    if(!phonemeURLs.length){
      speakCallback()
      return
    }

    const onEnd = () => {
      this.speakHumanPhonemes(phonemeURLs, speakCallback, errorCallback, run)
      return
    }
    
    const onError = (error) => {
      console.error('Error playing phoneme audio:', error);
      if (errorCallback) {
        errorCallback(error);
      } else {
        // If no error callback provided, skip to next phoneme
        onEnd();
      }
    }
    
    const url = phonemeURLs.shift()
    // Each mp3 gets its own window: a run of slow loads must not add up against one.
    this.noteVerbalSpeechProgress(run, '', 'phonemeStarted');
    this.props.speech.speakAudioFile({
      url,
      onStart: () => this.noteVerbalSpeechProgress(run, '', 'clipStarted'),
      onfinish: onEnd,
    })
  }

  startTransition() {
    // Clear the timeout safeguard since the transition is starting successfully
    if (this.verbalSpeechTimeoutId) {
      clearTimeout(this.verbalSpeechTimeoutId);
      this.verbalSpeechTimeoutId = null;
    }
    
    let state = this.state;
    state.metaData.type = "transition";
    this.setState(state);
  }

  onTransitionEnd(){
    this.setState({
      text: this.props.text,
      metaData: this.props.phraseMetaData,
    });
    this.props.startPhrase();
  }

  /**
   * All phrase types except for the ones listed below should auto start
   * @param {String} type
   * @returns
   */
  shouldAutoStartPhrase(type){

    if(this.props.assessmentMode){
      if(type === phraseTypes.UP_AND_DOWN){
        return false;
      }
    }

    return ![
      phraseTypes.WORD_PART_DELETION,
      phraseTypes.NONSENSE_WORD_REPETITION,
      phraseTypes.CHANGE_ONE_PART,
      phraseTypes.LETTER_FLIES,
      phraseTypes.ELKONIN
    ].includes(type);
  }

  handleStartOfPhrase(){
    const hasIntroductions = get(this.state, 'metaData.verbalIntroductions');
    //If we have no introductions AND no verbal instructions AND no video to play, then hit the startPhrase callback
    if(!this.state.metaData || (!hasIntroductions && !this.state.metaData.verbalInstructions && !this.state.metaData.transition && (!this.state.metaData.isVideo || this.state.metaData.type === "readAloudVideo"))){
      if(this.shouldAutoStartPhrase(this.state.metaData.type) && this.props.startPhrase){
        this.props.startPhrase();
      }
    }else{
      // present introduction first.
      if (this.state.metaData.verbalIntroductions){
				this.props.disableRepeatButton();
        const verbalIntroduction = this.isSessionFirstActivity() ? get(this.state, 'metaData.verbalIntroductions.full') : get(this.state, 'metaData.verbalIntroductions.short')
        // false: this advance runs handleStartOfPhrase, which arms on its own.
        this.speakVerbalInstructionsWithTriggers(verbalIntroduction, this.onFinishVerbalIntroductions, false);
      // then present instructions.
      }else if(this.state.metaData.verbalInstructions){
				this.props.disableRepeatButton();
        // An item with firstItemVerbalInstructions (items[0] of a beginningLetterSound testlet,
        // set in AssessmentOrchestrationService) does not land on the item when THIS run's
        // advance fires — onFinishVerbalInstructions queues a second clip instead. So neither this
        // run's normal end nor its safeguard arms; the inner run arms on its own end, which does
        // land. Decided per run, because the field stays set on the item for the inner run too.
        const advanceLandsOnItem = !this.props.phraseMetaData?.firstItemVerbalInstructions;
        this.speakVerbalInstructionsWithTriggers(
          this.state.metaData.verbalInstructions,
          this.onFinishVerbalInstructions,
          advanceLandsOnItem,
          { advanceQueuesSpeech: !advanceLandsOnItem },
        );
      }
      else if (this.state.metaData.transition){
        this.startTransition();
      }
    }
  }

  // keep track of the new set of errors so that we can save teachers' changes when we are in correction mode
  // this will never be called when we are in tutor mode
  updateErrors(index, newStatus){
    let newErrors = this.props.errors;
    if(index < newErrors.length && newStatus === statusOptions.UNREAD){
      newErrors = newErrors.slice(0, index); // remove all errors after this index
    }
    // if word is unread and being set to read, mark as read all previous unread words
    else if(index > newErrors.length-1){
      let count = index - newErrors.length;
      for(let i = 0; i <= count; i++){
        newErrors.push(false);
      }
      let prevUnreadCheck = false;
      let i = 1;
      while(!prevUnreadCheck){
        prevUnreadCheck = this.checkSetPreviousPhraseErrors(this.props.phraseIndex - i);
        i += 1;
      }
    }else {
      newErrors[index] = (newStatus === statusOptions.ERROR);
    }

    let newFlags = this.props.flaggedWords;
    if(newFlags){
      newFlags[index] = false;
    }

    this.props.setErrors(newErrors,newFlags,this.props.phraseIndex);
  }

  checkSetPreviousPhraseErrors(phraseIndex){
    if(phraseIndex < 0) return true;
    let phraseLength = this.props.phrases[phraseIndex].text.split(' ').filter(word => word != "" && word != " ").length;
    let errors = this.props.storyErrors[phraseIndex];
    if(phraseLength === errors.length) return true;
    else{
      let newErrors = errors.slice(0);
      let count = phraseLength - errors.length;
      for(let i = 0; i < count; i++){
        newErrors.push(false);
      }
      this.props.setErrors(newErrors, null, phraseIndex);
      return false;
    }
  }

  //wrap each word in a span with the appropriate classes to style it
  // keepSpaces: emit the literal space spans between words even in tap mode.
  // The flex-wrap tap layout (.phrase.tapPhrase) drops them and spaces via `gap`,
  // but callers that render the words into a plain block (e.g. PictureStoryPage's
  // .pagePhrase) need real spaces so the text can wrap.
  getMarkedPhrase(keepSpaces = false){
    const raw = this.state.text;
    const safe = raw == null ? '' : String(raw);
    // Tap-to-read only (TT-1116): render one word per SCORED word, so the tappable words
    // line up 1:1 with cleanAndSplit — which the tap gate, cursor, Next button and errors
    // array all index by. A plain space split diverges on em/en dashes. Every other mode
    // keeps the existing split, so this cannot affect them.
    let words = this.props.isTapToRead
      ? splitPhraseForTapDisplay(safe)
      : safe.split(' ').filter(word => word != "" && word != " ");
    let markedPhrase = [];
    const isTapMode = this.props.isTapToRead && this.props.isActive;
    const ghostIndex = this.props.currentWordIndex + 1;
    for(let i = 0; i < words.length; i++){
      let wordStatus = statusOptions.UNREAD;
      if(this.props.phraseWordStylingEnabled){ //disable word status styling in assessment mode
        if(this.props.interventionWordIndex == i){
          wordStatus = statusOptions.INTERVENTION;
        } else if(this.props.errors[i]){
          if(this.props.final){
            wordStatus = statusOptions.ERROR;
          } else {
            wordStatus = statusOptions.PROCESSING;
          }
        } else {
          // Word graying logic:
          // - Cursor-free mode + phrase in progress: words stay UNREAD (no word-by-word graying)
          // - Cursor-free mode + phrase completed (final): words marked SUCCESS (phrase-level graying)
          // - Non-cursor-free mode: words marked SUCCESS as they're read (word-by-word graying)
          if (i < this.props.errors.length && (!this.props.isCursorFree || this.props.final)){
            wordStatus = statusOptions.SUCCESS;
          }
        }

        //flagged status overrides all other statuses
        if(this.props.flaggedWords && this.props.flaggedWords[i]){
          wordStatus = statusOptions.FLAGGED;
        }
      }

      // Currently, the word is only clickable if we are in the teacher app,
      // and the word is either a scored word or an unread boundary word.
      // We will give setPlacement a value of 'true' in some student contexts
      // once we've worked through some UX concerns and placement manager issues.
      // TODO: set isClickable to true if setPlacement is true, after re-enabling placement
      let isClickable = (this.props.isCorrection && wordStatus !== statusOptions.BOUNDARY_WORD && !this.props.isDisabled) || (isTapMode && i === ghostIndex);

      markedPhrase.push(
        <Word
          canGrayOut={this.props.canGrayOut}
          phraseIndex={this.props.phraseIndex}
          updateErrors={this.updateErrors}
          status={wordStatus}
          highlighted={this.props.config.enableCurrentWordHighlight && i == this.props.highlightedWord && !this.props.isCursorFree}
          word={words[i]}
          key={"word"+i}
          index={i}
          clickable={isClickable}
          setPlacement={this.props.setPlacement}
          setDemoClick={this.props.setDemoClick}
          className={this.props.isCorrection ? " teacher" : ""}
          isEarlyReader={this.props.isEarlyReader}
          ghostNext={isTapMode && i === ghostIndex}
          tapMode={isTapMode}
          onTapToReadTap={this.props.onWordTapped}
        />
      );

      // separate with a space — except in the flex-wrap tap layout, where the
      // phrase is a flex row (Reader.scss .phrase.tapPhrase) and inter-word
      // spacing is the flex `gap`. Literal spaces would become stray flex items
      // and make line-end wrapping nondeterministic (TT-1006 last-word line-shift).
      // Callers rendering into a plain block pass keepSpaces=true so text wraps.
      if(i < words.length - 1 && (keepSpaces || !isTapMode)){
        markedPhrase.push(<span key={"space"+i}>{' '}</span>);  //TODO: think of a better way to do this
      }
    }
    return markedPhrase;
  }

  onVideoEnd(){
    console.log("in onVideoEnd");
    if(this.props.phraseMetaData.noInput){
      this.props.phraseTimeout();
    } else {
      this.props.startPhrase();
    }
  }

  // used to determine which introduction to use, full or short
  isSessionFirstActivity(){
    return this.props.activityIndex < 2
  }

  getCurrentWordMetadata(){
    return this.props.currentPhraseWordMetadata && this.props.currentPhraseWordMetadata[this.props.currentWordIndex];
  }

  handleImageLoadError(imageUrl, formattedImageUrl){
    logSessionInfo("image_load_error", { imageUrl, formattedImageUrl, testletId: this.props.story?.storyId });
  }

  handleAllImagesFailed(){
    logSessionInfo("all_images_load_failed", { testletId: this.props.story?.storyId });
    console.warn("All images failed to load in this testlet", this.props.story?.storyId);
    // check if we have already logged this testlet id in this session
    // this is to avoid logging the same testlet id multiple times to Sentry to avoid duplicate logs
    if(this.loggedTestletIds.includes(this.props.story?.storyId)){
      return;
    }
    this.loggedTestletIds.push(this.props.story?.storyId);
    Sentry.captureMessage("All images failed to load", {
      level: "warning",
    });
  }

  formativeScreenProps(verbalInstructions) {
    const instructionField = (field) =>
      this.state.metaData?.[field] ?? get(this.state, `metaData.instructions.${field}`);
    return {
      idleTimeoutOverrides: getIdleTimeoutsForStory(this.props.story),
      idleEncouragementText: instructionField('repeatVariation'),
      instructionRepeatable: instructionField('repeatable'),
      instructionMaxRepeats: instructionField('maxRepeats'),
      instructionRepeatVariation: instructionField('repeatVariation'),
      handleRepeatInstructions: this.props.handleRepeatInstructions,
      beginOverlayRepeatPlayback: this.props.beginOverlayRepeatPlayback,
      endOverlayRepeatPlayback: this.props.endOverlayRepeatPlayback,
      repeatInstructions: this.props.repeatInstructions,
      currentTriggerAction: this.state.currentIntroductionTrigger,
      currentTriggerData: this.state.currentTriggerData,
      onTriggerActionComplete: this.onTriggerActionComplete,
      verbalInstructions,
      // One concept, two prop names: MMC calls the tile pool its options. Aliased here so
      // item type five is one edit rather than three.
      deferPoolNarration: deferPoolNarrationToScript(verbalInstructions),
      deferOptionNarration: deferPoolNarrationToScript(verbalInstructions),
    };
  }

  getDisplayObject(){
    const isInstruction = (this.state.metaData && (this.state.metaData.verbalInstructions || this.state.metaData.textIntroductions)) ? true : false;
    const introduction = this.isSessionFirstActivity() ? get(this.state, "metaData.textIntroductions.full") : get(this.state, "metaData.textIntroductions.short")
    const verbalInstructions = this.state.metaData?.verbalInstructions
      ?? get(this.state, 'metaData.instructions.verbalInstructions');
    switch(this.state.metaData.type){
      case("wordPartDeletion"):
        return <WordPartDeletion
          speech={this.props.speech}
          transcript={this.props.transcript}
          setWillHandlePause={this.setWillHandlePause}
          paused={this.props.paused}
          startPhrase={this.props.startPhrase}
          pausePhrase={this.props.pausePhrase}
          resumePhrase={this.props.resumePhrase}
          phraseTimeout={this.props.phraseTimeout}
          doneHandlingPause={this.props.doneHandlingPause}
          targetPart={this.props.text}
          phraseMetadata={this.props.phraseMetaData}
          isPlayingVerbalInstructions={!!this.state.metaData.verbalInstructions}
          currentPageIndex={this.props.currentPageIndex}
          speakWithPhoneme={this.speakWithPhoneme}
          wordPartToDelete={this.state.metaData.wordPartToDelete || this.props.phraseMetaData.wordPartToDelete}
          />
      case("changeOnePart"):
        return <ChangeOnePartWrapper
          speech={this.props.speech}
          transcript={this.props.transcript}
          setWillHandlePause={this.setWillHandlePause}
          paused={this.props.paused}
          startPhrase={this.props.startPhrase}
          pausePhrase={this.props.pausePhrase}
          resumePhrase={this.props.resumePhrase}
          phraseTimeout={this.props.phraseTimeout}
          isFTUE={checkPhraseFtue(this.state.metaData.type) || !!this.props.phraseMetaData.playFTUE}
          doneHandlingPause={this.props.doneHandlingPause}
          originalWord={ this.props.phraseMetaData.instructions ? this.props.phraseMetaData.instructions.text : this.props.text }
          targetWord={this.props.text}
          phraseMetaData={this.props.phraseMetaData}
          isPlayingVerbalInstructions={!!this.state.metaData.verbalInstructions}
          currentPageIndex={this.props.currentPageIndex}
          isInstruction = {isInstruction}
          hasInstructions = {!!this.props.phraseMetaData.instructions && !this.shouldIgnoreInstructions()}
          currentWordIndex={this.props.currentWordIndex}
          setDisableInterventionsForPhrase={this.props.setDisableInterventionsForPhrase}
          setAllowFirstWordSkipOnPause={this.props.setAllowFirstWordSkipOnPause}
          />;
      case("upAndDown"):
        return (
          <UpAndDownWrapper
            speech={this.props.speech}
            phrase={this.state.text}
            isFTUE={checkPhraseFtue(this.state.metaData.type) || !!this.props.phraseMetaData.playFTUE}
            currentWordIndex = {this.props.currentWordIndex}
            setDisableInterventionsForPhrase = {this.props.setDisableInterventionsForPhrase}
            advanceOneWord = {this.props.advanceOneWord}
            skipWord = {this.props.skipWord}
            isInstruction = {isInstruction}
            hasInstructions = {!!this.props.phraseMetaData.instructions && !this.shouldIgnoreInstructions()}
            phraseMetadata={this.props.phraseMetaData}
            story={this.props.story}
            currentPageIndex={this.props.currentPageIndex}         
            currentPhraseWordMetadata={this.props.currentPhraseWordMetadata}   
            isPlayingVerbalInstructions={!!this.state.metaData.verbalInstructions}
            transcript={this.props.transcript}
            paused={this.props.paused}
            setWillHandlePause={this.setWillHandlePause}
            startPhrase={this.props.startPhrase}
            pausePhrase={this.props.pausePhrase}
            resumePhrase={this.props.resumePhrase}
            doneHandlingPause={this.props.doneHandlingPause}
            useWordImage={this.state.metaData.version === 2}
            imageURL={this.state.metaData.imageURL} 
          />
        );
      case("elkonin"):
        return (
          <ElkoninTutor
            speech={this.props.speech}
            phrase={this.state.text}
            currentWordIndex = {this.props.currentWordIndex}
            isFTUE={checkPhraseFtue(this.state.metaData.type) || !!this.props.phraseMetaData.playFTUE}
            setDisableInterventionsForPhrase = {this.props.setDisableInterventionsForPhrase}
            advanceOneWord = {this.props.advanceOneWord}
            exampleWord = {this.props.phraseMetaData.instructions && this.props.phraseMetaData.instructions.text}
            startPhrase = {this.props.startPhrase}
            paused={this.props.paused}
            pausePhrase={this.props.pausePhrase}
            resumePhrase={this.props.resumePhrase}
            setWillHandlePause={this.setWillHandlePause}
            currentPhraseWordMetadata={this.props.currentPhraseWordMetadata}
          />
        );
      case("nonsenseWordRepetition"):
        return <NonsenseWordRepetition
          speech={this.props.speech}
          transcript={this.props.transcript}
          setWillHandlePause={this.setWillHandlePause}
          paused={this.props.paused}
          startPhrase={this.props.startPhrase}
          pausePhrase={this.props.pausePhrase}
          resumePhrase={this.props.resumePhrase}
          phraseTimeout={this.props.phraseTimeout}
          doneHandlingPause={this.props.doneHandlingPause}
          nonsenseWord={this.props.text}
          phraseMetadata={this.props.phraseMetaData}
          isPlayingVerbalInstructions={!!this.state.metaData.verbalInstructions}
          currentPageIndex={this.props.currentPageIndex}
          />
      case("letterFlies"):
        return <LetterFliesWrapper
          speech={this.props.speech}
          startPhrase={this.props.startPhrase}
          isFTUE={checkPhraseFtue(this.state.metaData.type) || !!this.props.phraseMetaData.playFTUE}
          currentWordIndex={this.props.currentWordIndex}
          phraseMetadata={this.props.phraseMetaData}
          isInstruction={isInstruction}
          transcript={this.props.transcript}
          setWillHandlePause={this.setWillHandlePause}
          paused={this.props.paused}
          pausePhrase={this.props.pausePhrase}
          resumePhrase={this.props.resumePhrase}
          doneHandlingPause={this.props.doneHandlingPause}
          />
      case("intro"):
        const title = this.props.story.title;
        const author = this.props.story.author;
        let subtitle;
        const hasAuthor = author && author.trim() !== "";
        if (hasAuthor) {
          subtitle = `by ${author}`;
        }

        return <StoryIntro introduction={introduction} imageURL={this.state.metaData.imageURL} title={title} subtitle={subtitle} />
      case("RAN"):
      case("letterNameAndSounds"):
        return <RANItems image={this.state.metaData.imageURL} isInstruction={isInstruction}/>;
      case("sound"):
        let soundLetter = this.state.text;
        if (this.props.story?.type === aosTaskTypes.beginningLetterSound) {
          if (this.state.metaData?.imageURL) {
            return <PictureStoryPage imageURL={this.state.metaData.imageURL} />;
          } else {
            const word = soundLetter.includes("_sound") ? this.state.metaData?.startingWord : soundLetter; //TODO: do this in a less hacky way
            return <SplitItem left={word} isInstruction={isInstruction} type={this.props.story?.type} />;
          }
        }
        if (this.props.story?.type === aosTaskTypes.letterSound) {
          // display startingWord instead of phrase when available, since phrase cannot have spanish accents
          soundLetter = this.state.metaData?.startingWord || soundLetter;
        }
        soundLetter = soundLetter.replace("_sound_es","").replace("_sound",""); //TODO: do this in a less hacky way
        let leftString = soundLetter.toUpperCase();
        let rightString = soundLetter.toLowerCase();
        return <SplitItem left={leftString} right={rightString} isInstruction={isInstruction}/>;
      case("word"):
        let wordImage = this.props.disablePictures ? null : this.state.metaData.imageURL;
        return <SingleWord
          text={this.state.text}
          image={wordImage}
          isInstruction={isInstruction}
          isEarlyReader={this.props.isEarlyReader}
          speech={this.props.speech}
          transcript={this.props.transcript}
          setWillHandlePause={this.setWillHandlePause}
          paused={this.props.paused}
          startPhrase={this.props.startPhrase}
          pausePhrase={this.props.pausePhrase}
          resumePhrase={this.props.resumePhrase}
          phraseTimeout={this.props.phraseTimeout}
          doneHandlingPause={this.props.doneHandlingPause}
          currentPageIndex={this.props.currentPageIndex}
          advanceOneWord={this.props.advanceOneWord}
          setDisableInterventionsForPhrase = {this.props.setDisableInterventionsForPhrase}
          setAllowFirstWordSkipOnPause = {this.props.setAllowFirstWordSkipOnPause}
          triggerWordIntervention = {this.props.triggerWordIntervention}
          wordMetadata={this.getCurrentWordMetadata()}
        />;
      case("textInput"):
        if (this.state.metaData.version === 2) {
          return <Spelling 
            key={"spelling" + this.state.text} 
            word={this.state.text} 
            isInstruction={isInstruction} 
            isRepeating={this.props.isRepeating} 
            setTextResponse={this.props.setTextResponse} 
            speech={this.props.speech} 
            handleRepeatInstructions={this.props.handleRepeatInstructions} 
            repeatInstructions={this.props.repeatInstructions}
            setHideParentRepeatButton={this.props.setHideRepeatButton}
            currentIntroductionTrigger={this.state.currentIntroductionTrigger}
            useTyping={!this.state.metaData.letterDisplay}
            letterDisplay={this.state.metaData.letterDisplay}
          />;
        } else {
          return <TextInput 
            key={"textInput" + this.state.text} 
            text={this.state.text} 
            isInstruction={isInstruction} 
            isRepeating={this.props.isRepeating} 
            setTextResponse={this.props.setTextResponse} 
            speech={this.props.speech}
          />;
        }
      case("blendingSelection"):
        return <BlendingSelection
          videoURL={this.state.metaData.videoURL}
          speech={this.props.speech}
          onDone={(response) => {this.props.setTextResponse(response, true)}}
          options={this.state.metaData.options}
          isInstruction={isInstruction}
          playIntroduction={this.state.metaData.playComponentIntroductions}
          shouldExtendTimeout={this.props.configuredToExtendAssessmentTimeout}
          timeoutExtensionMultiplier={this.props.assessmentTimeoutExtensionMultiplier}
          onImageLoadError={this.handleImageLoadError.bind(this)}
          onAllImagesFailed={this.handleAllImagesFailed.bind(this)}
        />
      case("retell"):
        return <Retell
          isInstruction={isInstruction}
          videoURL={this.state.metaData.videoURL}
          speech={this.props.speech}
          onDone={() => {this.props.setTextResponse(NO_RESPONSE, true)}}
          storyId={this.props.story.storyId}
          activityId={this.props.activityId}
          phraseIndex={this.props.currentPhraseIndex}
          setRecordingProps={this.props.setRecordingProps}
          shouldUseAOS={this.props.shouldUseAOS}
					setHideParentRepeatButton={this.props.setHideRepeatButton}
          setHideParentListeningStatus={this.props.setHideListeningStatus}
        />
      case(phraseTypes.FREE_ANSWER):
        return <FreeAnswer
          key={'freeAnswer-' + (this.props.phraseMetaData?.itemId || this.props.text)}
          speech={this.props.speech}
          imageURL={this.state.metaData.imageURL}
          videoURL={this.state.metaData.videoURL}
          isInstruction={isInstruction}
          isRepeating={this.props.isRepeating}
          onDone={() => {this.props.setTextResponse(NO_RESPONSE, true)}}
          storyId={this.props.story.storyId}
          activityId={this.props.activityId}
          phraseIndex={this.props.currentPhraseIndex}
          setRecordingProps={this.props.setRecordingProps}
          shouldUseAOS={this.props.shouldUseAOS}
          shouldExtendTimeout={this.props.configuredToExtendAssessmentTimeout}
          timeoutExtensionMultiplier={this.props.assessmentTimeoutExtensionMultiplier}
          onImageLoadError={this.handleImageLoadError.bind(this)}
          setHideParentListeningStatus={this.props.setHideListeningStatus}
          setHideParentRepeatButton={this.props.setHideRepeatButton}
          handleRepeatInstructions={this.props.handleRepeatInstructions}
          repeatInstructions={this.props.repeatInstructions}
          currentIntroductionTrigger={this.state.currentIntroductionTrigger}
          postStimulusQuestion={
            this.state.metaData?.postStimulusQuestion
            ?? get(this.state, 'metaData.instructions.postStimulusQuestion')
            ?? get(this.props, 'phraseMetaData.instructions.postStimulusQuestion')
          }
        />
      case("video"):
        if((this.state.metaData.verbalInstructions) && getUserData().sessionData.isiPad){ // don't mount the video until we are done speaking on ipad to prevent volume jump
          return null;
        } else {
          return (
            <Video
              videoURL={this.state.metaData.videoURL}
              text={this.state.text}
              playVideo={!this.state.metaData.verbalInstructions}
              onDone={this.onVideoEnd.bind(this)}
              unmountWhenDone={getUserData().sessionData.isiPad && !this.props.isNextPhraseVideo}
              key="theOnlyDyslexiaVideo"
              ref="theOnlyDyslexiaVideo"
              speech={this.props.speech}
            />
          );
        }
      case("readAloud"):
        return <ReadAloud audioURL={this.state.metaData.audioURL} onAudioFinished={this.props.phraseTimeout}/>;
      case("quiz"):
        return (
          <Quiz
            doIntro={false}
            speech={this.props.speech}
            questions={this.state.metaData.questions}
            image={this.state.metaData.imageURL}
            onSubmit={(data) => {this.props.setTextResponse(data[0], true)}}
            sendMessage={this.props.sendMessage}
            submitButton={true}
            endOnCorrect={!this.props.assessmentMode}
            referenceStoryPhrases={this.props.story.referenceStoryPhrases}
            canViewReferenceStory={this.state.metaData.canViewReferenceStory}
            isInstruction={isInstruction}
            currentIntroductionTrigger={this.state.currentIntroductionTrigger}
            shouldExtendTimeout={this.props.configuredToExtendAssessmentTimeout}
            timeoutExtensionMultiplier={this.props.assessmentTimeoutExtensionMultiplier}
            // TODO: Reviewer: We may not need the last 2 conditions
            nonverbalFlow={this.props.isFirstAssessmentItemOfType && getUserData().nonverbal && this.props.story.type === 'readingComprehension' && this.props.story.referenceStoryPhrases && this.props.story.referenceStoryPhrases.length > 0}
          />
        );
      case("readAloudVideo"):
        if(this.state.metaData.verbalInstructions && getUserData().sessionData.isiPad){ // don't mount the video until we are done speaking on ipad to prevent volume jump
            return null;
          } else {
          return (
            <ReadAloudVideo
              assetURL={this.state.metaData.assetURL}
              onDone={() => {this.props.setTextResponse(NO_RESPONSE, true)}}
              unmountWhenDone={getUserData().sessionData.isiPad && !this.props.isNextPhraseVideo}
              speech={this.props.speech}
            />
          );
        }
      case("pictureStoryPage"):
        let imageURL = this.props.disablePictures ? null : this.state.metaData.imageURL;
        return (
          // keepSpaces: PictureStoryPage renders the words in a plain block
          // (.pagePhrase), not the flex-wrap tap container, so real spaces are
          // needed for the text to wrap instead of running off one line.
          <PictureStoryPage imageURL={imageURL} phrase={this.getMarkedPhrase(true)}/>
        );
      case("transition"):
        return <Transition type={this.state.metaData.transition} onEnd={this.onTransitionEnd.bind(this)} speech={this.props.speech} />
      case ("visualAttention"):
        return <VisualAttention
          isInstruction={isInstruction}
          targetOption={this.props.text}
          targetImage={this.props.phraseMetaData.imageURL}
          currentIntroductionTrigger={this.state.currentIntroductionTrigger}
          allOptions={this.props.phraseMetaData.options}
          gridWidth={this.props.phraseMetaData.gridWidth}
          repeatInstructions={this.props.repeatInstructions}
          speech={this.props.speech}
          setHideParentRepeatButton={this.props.setHideRepeatButton}
          setArrayResponse={this.props.setArrayResponse} 
          startPhrase={this.props.nonAudioStartPhrase}
         /> 
      case(phraseTypes.MMC):
      case(phraseTypes.GRAPHEME_PHONEME_CONVERSION):
      case(phraseTypes.VOCABULARY):
      case(phraseTypes.LISTENING_COMPREHENSION):
        // Everything that is not Formative renders the MMC frozen at Release 9.17.2026, with that
        // release's props, so legacy items look and behave exactly as they did before Adventures.
        if (this.props.story?.isFormative) {
          return <FormativeMultipleChoice
            key={'mmc-' + (this.props.phraseMetaData?.itemId || this.props.text)}
            speech={this.props.speech}
            setHideParentRepeatButton={this.props.setHideRepeatButton}
            options={this.state.metaData.options}
            videoURL={this.state.metaData.videoURL}
            imageURL={this.state.metaData.imageURL}
            imageInPrompt={this.state.metaData.type === phraseTypes.VOCABULARY}
            questionText={this.state.metaData.writtenInstructions}
            readOptionsAloud={this.state.metaData.readOptionsAloud}
            verticalOptions={this.state.metaData.verticalOptions}
            multiSelect={this.state.metaData.mode === MULTI_SELECT_MODE}
            locale={getStoryContentLocale(this.props.story)}
            isMarkupInstruction={isMarkupInstructionForStory(this.props.story)}
            isInstruction={isInstruction}
            isRepeating={this.props.isRepeating}
            onDone={(response) => {this.props.setTextResponse(response, true)}}
            shouldExtendTimeout={this.props.configuredToExtendAssessmentTimeout}
            timeoutExtensionMultiplier={this.props.assessmentTimeoutExtensionMultiplier}
            onImageLoadError={this.handleImageLoadError.bind(this)}
            onAllImagesFailed={this.handleAllImagesFailed.bind(this)}
            {...this.formativeScreenProps(verbalInstructions)}
          />
        }
        return <ModularMultipleChoice
          key={'mmc-' + (this.props.phraseMetaData?.itemId || this.props.text)}
          speech={this.props.speech}
          setHideParentRepeatButton={this.props.setHideRepeatButton}
          options={this.state.metaData.options}
          videoURL={this.state.metaData.videoURL}
          imageURL={this.state.metaData.imageURL}
          imageInPrompt={this.state.metaData.type === phraseTypes.VOCABULARY}
          questionText={this.state.metaData.writtenInstructions}
          readOptionsAloud={this.state.metaData.readOptionsAloud}
          verticalOptions={this.state.metaData.verticalOptions}
          locale={getStoryContentLocale(this.props.story)}
          isMarkupInstruction={isMarkupInstructionForStory(this.props.story)}
          isInstruction={isInstruction}
          isRepeating={this.props.isRepeating}
          onDone={(response) => {this.props.setTextResponse(response, true)}}
          shouldExtendTimeout={this.props.configuredToExtendAssessmentTimeout}
          timeoutExtensionMultiplier={this.props.assessmentTimeoutExtensionMultiplier}
          idleTimeoutOverrides={getIdleTimeouts(this.props.story.type)}
          onImageLoadError={this.handleImageLoadError.bind(this)}
          onAllImagesFailed={this.handleAllImagesFailed.bind(this)}
          idleEncouragementText={
            this.state.metaData?.repeatVariation
            ?? get(this.state, 'metaData.instructions.repeatVariation')
          }
          instructionRepeatable={
            this.state.metaData?.repeatable
            ?? get(this.state, 'metaData.instructions.repeatable')
          }
          instructionRepeatVariation={
            this.state.metaData?.repeatVariation
            ?? get(this.state, 'metaData.instructions.repeatVariation')
          }
          handleRepeatInstructions={this.props.handleRepeatInstructions}
          beginOverlayRepeatPlayback={this.props.beginOverlayRepeatPlayback}
          endOverlayRepeatPlayback={this.props.endOverlayRepeatPlayback}
          repeatInstructions={this.props.repeatInstructions}
          currentTriggerAction={this.state.currentIntroductionTrigger}
          currentTriggerData={this.state.currentTriggerData}
          onTriggerActionComplete={this.onTriggerActionComplete}
        />
      case(phraseTypes.BUCKETING):
        return <Bucketing
          key={'bucketing-' + (this.props.phraseMetaData?.itemId || this.props.text)}
          speech={this.props.speech}
          setHideParentRepeatButton={this.props.setHideRepeatButton}
          options={this.state.metaData.options}
          dropZones={this.state.metaData.dropZones}
          videoURL={this.state.metaData.videoURL}
          imageURL={this.state.metaData.imageURL}
          readOptionsAloud={this.state.metaData.readOptionsAloud}
          isInstruction={isInstruction}
          isRepeating={this.props.isRepeating}
          onDone={(response) => {this.props.setEncodedResponse(response, true)}}
          shouldExtendTimeout={this.props.configuredToExtendAssessmentTimeout}
          timeoutExtensionMultiplier={this.props.assessmentTimeoutExtensionMultiplier}
          {...this.formativeScreenProps(verbalInstructions)}
        />
      case(phraseTypes.SEQUENCING):
        return <Sequencing
          key={'sequencing-' + (this.props.phraseMetaData?.itemId || this.props.text)}
          speech={this.props.speech}
          setHideParentRepeatButton={this.props.setHideRepeatButton}
          options={this.state.metaData.options}
          dropZones={this.state.metaData.dropZones}
          videoURL={this.state.metaData.videoURL}
          imageURL={this.state.metaData.imageURL}
          backgroundImageUrl={this.state.metaData.backgroundImageUrl}
          readOptionsAloud={this.state.metaData.readOptionsAloud}
          isInstruction={isInstruction}
          isRepeating={this.props.isRepeating}
          onDone={(response) => {this.props.setEncodedResponse(response, true)}}
          shouldExtendTimeout={this.props.configuredToExtendAssessmentTimeout}
          timeoutExtensionMultiplier={this.props.assessmentTimeoutExtensionMultiplier}
          {...this.formativeScreenProps(verbalInstructions)}
        />
      case(phraseTypes.SENTENCE_BUILDER):
        return <SentenceBuilder
          key={'sentenceBuilder-' + (this.props.phraseMetaData?.itemId || this.state.text || '')}
          speech={this.props.speech}
          writtenInstructions={this.state.metaData.writtenInstructions}
          wordDisplay={this.state.metaData.wordDisplay}
          isInstruction={isInstruction}
          isRepeating={this.props.isRepeating}
          setTextResponse={this.props.setTextResponse}
          handleRepeatInstructions={this.props.handleRepeatInstructions}
          repeatInstructions={this.props.repeatInstructions}
          setHideParentRepeatButton={this.props.setHideRepeatButton}
          currentTriggerAction={this.state.currentIntroductionTrigger}
          currentTriggerData={this.state.currentTriggerData}
          onTriggerActionComplete={this.onTriggerActionComplete}
          idleEncouragementText={
            this.state.metaData?.repeatVariation
            ?? get(this.state, 'metaData.instructions.repeatVariation')
          }
          shouldExtendTimeout={this.props.configuredToExtendAssessmentTimeout}
          timeoutExtensionMultiplier={this.props.assessmentTimeoutExtensionMultiplier}
          idleTimeoutOverrides={getIdleTimeoutsForStory(this.props.story)}
        />
      default:
        return this.getMarkedPhrase();
    }
  }

  render() {
    let markedPhrase = this.getMarkedPhrase();

    let storyLength = markedPhrase.length / 2; // divide by 2 because we are counting spaces. This is not 100% accurate since there is no trailing space, but it is close enough for our purposes

    // if this is such a long phrase that it would overflow reader, make the font smaller.
    // this will happen with longer stories in the assessment use case. For now, we will size it down a fixed amount.
    // In the future we can bring in screen size etc
    const shortStoryCutoff = 95;
    const longStoryCutoff = 170;
    const hasImage = this.state.metaData.imageURL;
    
    // Handle isListening state - only apply to the active phrase
    const isAtStartOfPhrase = this.props.currentWordIndex === 0;
    
    // Hide: active && !listening && waitingForAsrConnection && at start of phrase && !assessmentMode
    const shouldHideContent = !this.props.assessmentMode &&
                              this.props.isActive && 
                              !this.props.isListening && 
                              this.props.waitingForAsrConnection && 
                              isAtStartOfPhrase;
    
    // Gray: !assessmentMode && active && !listening && endOfPhrase && !modalOpen && (!at start of phrase OR !waitingForAsrConnection)
    const shouldGrayContent = !this.props.assessmentMode &&
                              this.props.isActive && 
                              !this.props.isListening && 
                              this.props.endOfPhrase &&
                              !this.props.modalOpen;
    
    // TT-1006: in tap-to-read mode the phrase is sized to the screen (large font +
    // generous line spacing via .tapPhrase), NOT by story word count — so the
    // shortStory/longStory cutoffs are bypassed here.
    const isTapToRead = this.props.isTapToRead;

    let phraseClasses = cx({
      "phrase": true,
      "pictureStory": hasImage,
      "shortStory": !isTapToRead && storyLength < shortStoryCutoff && !hasImage,
      "longStory": !isTapToRead && storyLength > longStoryCutoff && !hasImage,
      "tapPhrase": isTapToRead,
      "highlightErrors": false,//process.env.STACK !== "PRODUCTION",
      [this.props.className]: this.props.className && true,
      "fullWidth": FULLBLEED_ASSESSMENT_TYPES.includes(this.state.metaData.type),
      "fullHeight": FULLHEIGHT_ASSESSMENT_TYPES.includes(this.state.metaData.type),
      "inactive": shouldGrayContent,
      "hide": shouldHideContent
    });

    const phraseContentClasses = cx({
      "phraseContent": true,
      "cursorFree": this.props.isCursorFree
    });

    // Modifier only — NextButton supplies the base .phraseNextButton class.
    const nextButtonClasses = cx({
      "showNextButton": this.props.showNextButton && this.props.isActive
    });

    return(
      <div
        className={phraseClasses}
        ref={isTapToRead ? this.tapPhraseRef : undefined}
        style={isTapToRead ? { '--tap-font-scale': this.state.tapFontScale } : undefined}
      >
        {this.props.isCursorFree ? (
          // Cursor-free mode: wrap in flexbox container for Next button layout
          <div className={phraseContentClasses}>
            <div className="phraseText">
              {this.getDisplayObject()}
            </div>
            <NextButton
              className={nextButtonClasses}
              onClick={this.props.onNextButtonClick}
            />
          </div>
        ) : (
          // Normal mode: direct rendering, no wrapper divs
          this.getDisplayObject()
        )}
      </div>
    );
  }
}

export default withCurrentStoryWordMetadata(Phrase);
